import { faCheck, faXmark } from '@fortawesome/pro-regular-svg-icons';
import type { OrganizationUserDto } from '@uturn/api/v1';
import { useFetchOrganizationById } from '@uturn/api/v1';
import { MetaDataContext } from '@uturn/portal/context';
import {
	AccordionContent,
	AccordionTrigger,
	Button,
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	Icon,
	MultiSelectBadge,
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@uturn/ui-kit';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { twJoin } from 'tailwind-merge';
import { FilterContext } from './context';
import { toNumberArray } from './utils-filter-params';

export function UserFilter() {
	const { filters, setFilters } = useContext(FilterContext);
	const { t } = useTranslation();
	const { metadata } = useContext(MetaDataContext);

	const orgId = metadata?.user?.organisation?.id ?? 0;

	const { data: users, isSuccess } = useFetchOrganizationById(orgId, {
		query: {
			enabled: orgId > 0,
			refetchOnWindowFocus: false,
			select: (data) => data.data.users,
		},
	});

	const types =
		typeof filters['filter.userIds'] === 'string'
			? [filters['filter.userIds']]
			: (filters['filter.userIds'] ?? []);

	const selectedUserIds = toNumberArray(filters['filter.userIds']);

	const handleOnChange = (userId: number, select: boolean) => {
		if (select) {
			if (!selectedUserIds.includes(userId)) {
				setFilters({ 'filter.userIds': [...selectedUserIds, userId] });
			}
		} else {
			const next = selectedUserIds.filter((id) => id !== userId);
			setFilters({ 'filter.userIds': next });
		}
	};

	const SelectedUsers = ({ userIds }: { userIds: number[] }) => {
		if (!userIds || userIds.length === 0) {
			return null;
		}

		return userIds.map((userId) => {
			const selectedUser = users?.find((u) => u.id === userId);
			if (!selectedUser) {
				return null;
			}

			return (
				<MultiSelectBadge
					key={userId}
					label={`${selectedUser?.firstName} ${selectedUser?.lastName}`}
					removeIcon={<Icon icon={faXmark} />}
					removeValue={() => handleOnChange(userId, false)}
					disableRemoveValue={false}
				/>
			);
		});
	};

	if (!isSuccess) {
		return null;
	}

	return (
		<>
			<AccordionTrigger>
				{t('pages.shipments.filters.users.title', 'Users')}
			</AccordionTrigger>
			<AccordionContent>
				<div className="grid gap-1">
					<Popover>
						<PopoverTrigger asChild>
							<Button
								variant="input"
								role="combobox"
								className={twJoin(
									'w-full',
									types && types.length < 1 && 'text-muted-foreground',
								)}
							>
								<div className="flex">
									{selectedUserIds.length < 1 ? (
										'Select users'
									) : (
										<SelectedUsers userIds={selectedUserIds} />
									)}
								</div>
							</Button>
						</PopoverTrigger>
						<PopoverContent align="start" className="w-full p-0">
							<Command className="max-h-[300px]">
								<CommandInput
									placeholder={t(
										'pages.shipments.filters.users.placeholder',
										'Search users...',
									).toString()}
								/>
								<CommandEmpty>
									{t(
										'pages.shipments.filters.users.no-results',
										'No users found.',
									)}
								</CommandEmpty>
								<CommandGroup className="overflow-y-scroll">
									{users.map((user) => {
										const isSelected = types.indexOf(user.id) >= 0;

										return (
											<CommandItem
												key={user.id.toString()}
												value={`${user.firstName} ${user.lastName}`}
												onSelect={() => handleOnChange(user.id, !isSelected)}
											>
												<Icon
													icon={faCheck}
													className={twJoin(
														'mr-2 h-4 w-4',
														isSelected ? 'opacity-100' : 'opacity-0',
													)}
												/>
												{user.firstName} {user.lastName}
											</CommandItem>
										);
									})}
								</CommandGroup>
							</Command>
						</PopoverContent>
					</Popover>
				</div>
			</AccordionContent>
		</>
	);
}
